import styled from 'styled-components';

import { CFG, CFG_CHROME } from '../../../../../../theme/configurator-tokens';
import { SPACE } from '../../../../../../theme/tokens';
import { cardHover } from '../../../../../../theme/configurator-keyframes';
import { Text } from '../../../../../../components';
import { PaneCard } from '../../../../components/wizard';
import { DESIGN, DESIGN_GRADIENTS } from '../../design.tokens';

/* --- Shell ----------------------------------------------------------------- */
export const Overlay = styled.div`
  position: absolute;
  inset: 0;
  z-index: 30;
  display: flex;
  flex-direction: column;
  border-radius: 20px;
  background: ${CFG_CHROME.sidebarBg};
`;

export const Header = styled.header`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 16px;
`;

export const BackButton = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  cursor: pointer;
  border: 0;
  background: transparent;
  padding: 0;
`;

export const BackLabel = styled(Text)`
  font-size: 16px;
  line-height: 15.5px;
  color: ${CFG.base50};
`;

export const HeaderMeta = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  align-items: flex-end;
  gap: 2px;
`;

export const HeaderTitle = styled(Text)`
  font-size: 18px;
  font-weight: 700;
  line-height: 1.1;
  color: ${CFG.base200};
`;

export const HeaderSub = styled(Text)`
  font-size: 14px;
  line-height: 16px;
  color: ${CFG.base500};
`;

/* --- Body ------------------------------------------------------------------ */
export const Body = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 12px 8px 16px;

  scrollbar-width: thin;
  &::-webkit-scrollbar {
    width: 6px;
  }
  &::-webkit-scrollbar-thumb {
    border-radius: 9999px;
    background: ${CFG.hairlineStrong};
  }
`;

export const CurrentLabel = styled(Text)`
  display: block;
  padding: 0 8px 8px;
  font-size: 14px;
  line-height: 16px;
  color: ${CFG.base400};
`;

/** PaneCard with the catalog's wider 24px gap. */
export const FilterPane = styled(PaneCard)`
  gap: 24px;
  margin-top: 16px;
`;

/* --- Current theme card ---------------------------------------------------- */
export const CurrentCard = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 48px;
  overflow: hidden;
  border-radius: 22px;
  border: 2px solid ${CFG.brand};
  padding: 8px;
  background-image: ${DESIGN_GRADIENTS.featuredWash};
`;

export const CardOverlay = styled.span<{ $gradient: string }>`
  pointer-events: none;
  position: absolute;
  inset: 0;
  background-image: ${(p) => p.$gradient};
`;

export const CardRow = styled.span`
  position: relative;
  display: flex;
  align-items: center;
  gap: 12px;
`;

export const CurrentLogo = styled.span`
  flex-shrink: 0;
  width: 84px;
  height: 84px;
  overflow: hidden;
  border-radius: 16px;
  background: ${DESIGN.logoTile};
`;

export const LogoImg = styled.img`
  width: 100%;
  height: 100%;
  object-fit: contain;
`;

export const CardMeta = styled.span`
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: 6px;
`;

/** Theme name + optional "New" badge on one line (current card + result rows). */
export const NameRow = styled.span`
  display: flex;
  /* Baseline, not center: the name here is a <p> that keeps its default bottom margin
     (v0's name/subtitle spacing relies on it), which would pull a centred badge low. */
  align-items: baseline;
  gap: ${SPACE[8]};
`;

export const CurrentName = styled(Text)`
  font-size: 18px;
  font-weight: 400;
  line-height: 1.2;
  color: ${CFG.base200};
`;

export const Subtitle = styled(Text)`
  font-size: 14px;
  line-height: 16px;
  color: ${DESIGN.subtitle};
`;

/* --- Results --------------------------------------------------------------- */
export const Results = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

export const ResultsCount = styled(Text)`
  padding: 0 8px;
  font-size: 16px;
  font-weight: 500;
  line-height: 1.1;
  color: ${CFG.base200};
`;

export const ResultsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

export const ResultCard = styled.button`
  position: relative;
  display: flex;
  height: 80px;
  width: 100%;
  cursor: pointer;
  align-items: center;
  gap: 13.6px;
  overflow: hidden;
  border-radius: 22px;
  border: 1.5px solid ${DESIGN.cardBorder};
  padding: 8px;
  text-align: left;
  background: transparent;

  ${cardHover}
`;

export const ResultIcon = styled.span`
  position: relative;
  display: block;
  flex-shrink: 0;
  width: 64px;
  height: 64px;
  overflow: hidden;
  border-radius: 16px;
  border: 0.78px solid ${DESIGN.catalogResultIconBorder};
  background-image: ${DESIGN_GRADIENTS.resultIconFill};
`;

export const ResultIconImg = styled.img`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

export const ResultMeta = styled.span`
  position: relative;
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: 4px;
`;

export const ResultName = styled(Text)`
  font-size: 16px;
  font-weight: 300;
  line-height: 1.2;
  color: ${CFG.base200};
`;

export const ResultSubtitle = styled(Subtitle)`
  font-size: 14px;
`;

/* --- Footer ---------------------------------------------------------------- */
export const Footer = styled.footer`
  display: flex;
  height: 72px;
  align-items: center;
  padding: 0 16px;
  border-bottom-left-radius: 20px;
  border-bottom-right-radius: 20px;
  background: ${CFG_CHROME.footerBg};
`;

export const UseThemeButton = styled.button`
  display: flex;
  height: 40px;
  width: 100%;
  cursor: pointer;
  align-items: center;
  justify-content: center;
  gap: 8px;
  border-radius: 9999px;
  /* Cursor-following hover glow + lit hairline ring (shared with the CTA Button). */
  ${cardHover}
  border: 1px solid ${DESIGN.useThemeBorder};
  background: ${DESIGN.useThemeBg};
  font-size: 14px;
  font-weight: 500;
  color: ${DESIGN.useThemeText};
  transition: background-color 0.2s ease;

  &:hover {
    background: ${DESIGN.useThemeBgHover};
  }
`;
